import React from "react";
import { InlineMath } from "react-katex";
import {
  makeClosedTask,
  normalizeMay2026Variant,
  pick,
  shiftedSquare,
} from "../../../../matura/MaturaMay2026ClosedTaskSupport";

// Oryginał CKE pozostaje niezmieniony. Warianty treningowe równomiernie
// reprezentują wszystkie cztery układy odpowiedzi: PP, PF, FP i FF.
const ORIGINAL = {
  h: 1,
  k: -3,
  r: 5,
  px: 4,
  py: -7,
  point: true,
  claimedH: 1,
  claimedK: -3,
  claimedRhs: 5,
  equation: false,
};

const VARIANTS = [
  { h: 2, k: 1, r: 5, px: 5, py: 5, point: true, claimedH: 2, claimedK: 1, claimedRhs: 25, equation: true },
  { h: -1, k: 2, r: 4, px: 3, py: 2, point: true, claimedH: -1, claimedK: 2, claimedRhs: 4, equation: false },
  { h: 3, k: -2, r: 5, px: 6, py: 1, point: false, claimedH: 3, claimedK: -2, claimedRhs: 25, equation: true },
  { h: -2, k: 1, r: 5, px: 1, py: 4, point: false, claimedH: 2, claimedK: 1, claimedRhs: 25, equation: false },
  { h: -3, k: 2, r: 5, px: 0, py: 6, point: true, claimedH: -3, claimedK: 2, claimedRhs: 25, equation: true },
  { h: 1, k: -2, r: 5, px: 4, py: 2, point: true, claimedH: 1, claimedK: 2, claimedRhs: 25, equation: false },
  { h: -1, k: -2, r: 4, px: 2, py: 0, point: false, claimedH: -1, claimedK: -2, claimedRhs: 16, equation: true },
  { h: -2, k: -3, r: 5, px: 2, py: -1, point: false, claimedH: 2, claimedK: 3, claimedRhs: 25, equation: false },
  { h: 4, k: -1, r: 5, px: 8, py: 2, point: true, claimedH: 4, claimedK: -1, claimedRhs: 25, equation: true },
  { h: -4, k: 2, r: 5, px: -1, py: 6, point: true, claimedH: 4, claimedK: -2, claimedRhs: 25, equation: false },
  { h: 2, k: 3, r: 5, px: 4, py: 5, point: false, claimedH: 2, claimedK: 3, claimedRhs: 25, equation: true },
  { h: -3, k: -1, r: 4, px: 0, py: 1, point: false, claimedH: -3, claimedK: -1, claimedRhs: 4, equation: false },
];

const coordinateLatex = (value) => (value < 0 ? `(${value})` : String(value));
const squaredValueLatex = (value) => (value < 0 ? `(${value})^2` : `${value}^2`);

function distanceExplanation(data) {
  const dx = data.px - data.h;
  const dy = data.py - data.k;
  const distanceSquared = dx ** 2 + dy ** 2;
  const integerDistance = Number.isInteger(Math.sqrt(distanceSquared))
    ? String(Math.sqrt(distanceSquared))
    : null;
  const distanceLatex = integerDistance || `\\sqrt{${distanceSquared}}`;
  const comparison = data.point ? "=" : "\\neq";

  return `\\begin{aligned}
\\lvert SA\\rvert&=\\sqrt{(x_A-x_S)^2+(y_A-y_S)^2}\\\\
&=\\sqrt{(${coordinateLatex(data.px)}-${coordinateLatex(data.h)})^2+(${coordinateLatex(data.py)}-${coordinateLatex(data.k)})^2}\\\\
&=\\sqrt{${squaredValueLatex(dx)}+${squaredValueLatex(dy)}}=\\sqrt{${distanceSquared}}${integerDistance ? `=${integerDistance}` : ""},\\\\
r&=${data.r},\\\\
\\lvert SA\\rvert&${comparison} r\\quad (${distanceLatex}${comparison} ${data.r}).
\\end{aligned}`;
}

function equationExplanation(data, claimedEquation) {
  const correctEquation = `${shiftedSquare("x", data.h)}+${shiftedSquare("y", data.k)}=${data.r ** 2}`;
  const verdict = data.equation
    ? "Oba równania są zgodne, więc drugie stwierdzenie jest prawdziwe."
    : "Równania nie są zgodne, więc drugie stwierdzenie jest fałszywe.";

  return [
    { text: "Korzystamy z postaci kanonicznej okręgu:" },
    { latex: "(x-x_S)^2+(y-y_S)^2=r^2" },
    { text: "Po podstawieniu współrzędnych środka i promienia otrzymujemy:" },
    { latex: correctEquation },
    { text: "W drugim stwierdzeniu zapisano równanie:" },
    { latex: claimedEquation },
    { text: verdict },
  ];
}

function pointStatementVerdict(data) {
  return data.point
    ? "Odległość punktu od środka jest równa promieniowi, więc pierwsze stwierdzenie jest prawdziwe."
    : "Odległość punktu od środka nie jest równa promieniowi, więc pierwsze stwierdzenie jest fałszywe.";
}

export function generateTask(options = {}) {
  const variant = normalizeMay2026Variant(options);
  const data = variant === "original" ? ORIGINAL : pick(VARIANTS);
  const equation = `${shiftedSquare("x", data.claimedH)}+${shiftedSquare("y", data.claimedK)}=${data.claimedRhs}`;

  return makeClosedTask({
    type: "MaturaCirclePointEquationTruthClosedTask",
    taskNumber: "25",
    variant,
    tags: ["geometria analityczna", "okrag"],
    data: {
      answerMode: "truth",
      statementTruth: [data.point, data.equation],
      circleTruthData: { ...data, claimedEquation: equation },
      instruction: "Oceń prawdziwość poniższych stwierdzeń.",
      prompt: (
        <div>
          <p>
            Dany jest okrąg o środku <InlineMath math={`S=(${data.h},${data.k})`} /> i promieniu{" "}
            <InlineMath math={String(data.r)} />.
          </p>
          <div className="matura-truth-statement-list">
            <div className="matura-truth-statement">
              <span className="matura-truth-statement__number"><InlineMath math="1." /></span>
              <span>Punkt <InlineMath math={`A=(${data.px},${data.py})`} /> leży na okręgu.</span>
            </div>
            <div className="matura-truth-statement">
              <span className="matura-truth-statement__number"><InlineMath math="2." /></span>
              <span>Okrąg jest opisany równaniem <InlineMath math={equation} />.</span>
            </div>
          </div>
        </div>
      ),
      explanationSteps: [
        {
          title: "Obliczamy odległość punktu od środka i porównujemy ją z promieniem.",
          latex: distanceExplanation(data),
          parts: [{ text: pointStatementVerdict(data) }],
        },
        {
          title: "Układamy równanie okręgu z podanych danych i porównujemy je ze stwierdzeniem.",
          parts: equationExplanation(data, equation),
        },
      ],
    },
  });
}

export {
  checkClosedAnswer as checkAnswer,
  renderClosedAnswer as renderAnswer,
  renderClosedExplanation as renderExplanation,
  renderClosedInput as renderInput,
  renderClosedTask as renderTask,
  renderClosedUserAnswer as renderUserAnswer,
} from "../../../../matura/MaturaMay2026ClosedTaskSupport";
